// F-492 (UAT, URS_6.8) — what the X on an organization row in "Who can see my data" does.
//
// ⭐ THE X IS THE PATIENT'S ONE WAY TO LEAVE AN ORGANIZATION. On an accepted row it LEAVES:
// `useMyOrgRelationship().leave` → `DELETE /me/organizations/{id}/association`, the kernel route
// that ends the link whoever began it (ACDPA-315), and which re-reads `GET /me` so the "Your
// equipment provider" card follows. Then the directory re-reads too.
//
// ⛔ It used to call `useOrgSharing().unshare` → `DELETE .../share`, which the kernel scopes to
// shares the PATIENT began (an org-created link no-ops with a 204) and which re-read only the
// directory — so the card went on naming the organization: "the organization is still assigned".
//
// ⚠️ A PENDING row is a request, not an association, so there is nothing to leave: its X withdraws
// the request (`unshare` — the only route that can), exactly as before.

import { useCallback, useState } from "react";
import { ShareableOrg } from "../../../services/orgSharing";
import { UseMyOrgRelationship } from "./useMyOrgRelationship";
import { shareStatusOf, UseOrgSharing } from "./useOrgSharing";

export interface UseOrgRemoval {
  /** The row whose X was tapped (its confirmation is open), or null. */
  target: ShareableOrg | null;
  request: (org: ShareableOrg) => void;
  cancel: () => void;
  /** Leave (accepted) or withdraw (pending) the target, then close. */
  confirm: () => Promise<void>;
}

export const useOrgRemoval = (
  rel: Pick<UseMyOrgRelationship, "leave">,
  orgSharing: Pick<UseOrgSharing, "unshare" | "reload">,
): UseOrgRemoval => {
  const [target, setTarget] = useState<ShareableOrg | null>(null);

  const request = useCallback((org: ShareableOrg) => setTarget(org), []);
  const cancel = useCallback(() => setTarget(null), []);

  const confirm = useCallback(async () => {
    const org = target;
    setTarget(null);
    if (!org) return;
    if (shareStatusOf(org) === "pending") {
      await orgSharing.unshare(org);
      return;
    }
    if (await rel.leave(org.org_id)) await orgSharing.reload();
  }, [target, rel, orgSharing]);

  return { target, request, cancel, confirm };
};
